import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Progress } from "reactstrap";
import { LoadingSpinnerWembii } from "../LoadingSpinnerWembii";
import {
  SUMMARY_PLATFORM_COLORS,
  SUMMARY_PLATFORM_LABEL_KEYS,
} from "./summary/summaryPlatformColors";
import { SummaryPlatform } from "./summary/summaryAnalyticsTypes";
import "./SummaryAnalytics.scss";

const PLATFORM_ICONS: Record<
  SummaryPlatform,
  { prefix: "fab"; name: "facebook" | "instagram" | "tiktok" | "linkedin" }
> = {
  facebook: { prefix: "fab", name: "facebook" },
  instagram: { prefix: "fab", name: "instagram" },
  tiktok: { prefix: "fab", name: "tiktok" },
  linkedinBusiness: { prefix: "fab", name: "linkedin" },
};

interface Props {
  platforms: SummaryPlatform[];
}

export const SummaryAnalyticsLoading = ({ platforms }: Props) => {
  const intl = useIntl();
  const [activeStep, setActiveStep] = useState(0);
  const [statusIndex, setStatusIndex] = useState(0);

  const statusMessages = useMemo(
    () => [
      intl.formatMessage({ id: "app.analytics.summary.loadingStatusMetrics" }),
      intl.formatMessage({ id: "app.analytics.summary.loadingStatusPosts" }),
      intl.formatMessage({ id: "app.analytics.summary.loadingStatusCharts" }),
    ],
    [intl]
  );

  useEffect(() => {
    if (platforms.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setActiveStep((prev) => (prev + 1) % platforms.length);
    }, 1600);
    return () => window.clearInterval(timer);
  }, [platforms.length]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setStatusIndex((prev) => (prev + 1) % statusMessages.length);
    }, 2200);
    return () => window.clearInterval(timer);
  }, [statusMessages]);

  const totalSteps = Math.max(platforms.length, 1);
  const progress = Math.min(
    100,
    Math.round(((activeStep + 1) / totalSteps) * 88) + 8
  );
  const activePlatform = platforms[activeStep];

  return (
    <div className="summary-analytics-loading" aria-busy="true" aria-live="polite">
      <div className="summary-analytics-loading__banner">
        <div className="summary-analytics-loading__banner-icon">
          <FontAwesomeIcon icon={["fas", "chart-line"]} />
        </div>
        <div className="summary-analytics-loading__banner-copy">
          <span className="summary-analytics-loading__banner-eyebrow">
            {intl.formatMessage({ id: "app.analytics.summary.loadingEyebrow" })}
          </span>
          <h3 className="summary-analytics-loading__title mb-1 text-white">
            {intl.formatMessage({ id: "app.analytics.summary.loadingTitle" })}
          </h3>
          <p className="summary-analytics-loading__subtitle mb-0 text-white-50">
            {intl.formatMessage({ id: "app.analytics.summary.loadingSubtitle" })}
          </p>
        </div>
      </div>

      <div className="summary-analytics-loading__hero">
        <LoadingSpinnerWembii active size="md" />
        <div className="summary-analytics-loading__copy">
          <p className="summary-analytics-loading__hint mb-0">
            {intl.formatMessage({ id: "app.analytics.summary.loadingHint" })}
          </p>
        </div>
      </div>

      {platforms.length > 0 ? (
        <div className="summary-analytics-loading__platforms">
          {platforms.map((platform, index) => {
            const icon = PLATFORM_ICONS[platform];
            const isActive = index === activeStep;
            const isDone = index < activeStep;
            return (
              <div
                key={platform}
                className={cn("summary-analytics-loading__platform", {
                  "summary-analytics-loading__platform--active": isActive,
                  "summary-analytics-loading__platform--done": isDone,
                })}
              >
                <span
                  className="summary-analytics-loading__platform-icon"
                  style={{ backgroundColor: SUMMARY_PLATFORM_COLORS[platform] }}
                >
                  <FontAwesomeIcon icon={[icon.prefix, icon.name]} />
                </span>
                <span className="summary-analytics-loading__platform-label">
                  {intl.formatMessage({ id: SUMMARY_PLATFORM_LABEL_KEYS[platform] })}
                </span>
                {isActive ? (
                  <span className="summary-analytics-loading__platform-status">
                    {intl.formatMessage({ id: "app.analytics.summary.loadingFetching" })}
                  </span>
                ) : isDone ? (
                  <FontAwesomeIcon
                    icon={["fas", "check-circle"]}
                    className="summary-analytics-loading__platform-check"
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      <div className="summary-analytics-loading__progress-meta">
        <span>
          {intl.formatMessage(
            { id: "app.analytics.summary.loadingProgress" },
            {
              current: Math.min(activeStep + 1, totalSteps),
              total: totalSteps,
            }
          )}
        </span>
        <span className="summary-analytics-loading__progress-value">{progress}%</span>
      </div>

      <Progress
        className="summary-analytics-loading__progress"
        value={progress}
        max={100}
        animated
        color="primary"
      />

      <p className="summary-analytics-loading__status">
        {activePlatform
          ? intl.formatMessage(
              { id: "app.analytics.summary.loadingStatusNetwork" },
              {
                network: intl.formatMessage({
                  id: SUMMARY_PLATFORM_LABEL_KEYS[activePlatform],
                }),
              }
            )
          : statusMessages[statusIndex]}
      </p>

      <div className="summary-analytics-loading__skeleton" aria-hidden>
        <div className="summary-analytics-loading__skeleton-tabs">
          <span className="summary-analytics-loading__shimmer" />
          <span className="summary-analytics-loading__shimmer summary-analytics-loading__shimmer--short" />
        </div>
        <div className="summary-analytics-loading__skeleton-card">
          <span className="summary-analytics-loading__shimmer summary-analytics-loading__shimmer--title" />
          <span className="summary-analytics-loading__shimmer summary-analytics-loading__shimmer--value" />
          <span className="summary-analytics-loading__shimmer summary-analytics-loading__shimmer--bar" />
        </div>
        <div className="summary-analytics-loading__skeleton-pills">
          {(platforms.length ? platforms : (["facebook", "instagram"] as SummaryPlatform[])).map(
            (platform) => (
              <span
                key={platform}
                className="summary-analytics-loading__shimmer summary-analytics-loading__shimmer--pill"
              />
            )
          )}
        </div>
        <div className="summary-analytics-loading__skeleton-chart">
          <span className="summary-analytics-loading__shimmer summary-analytics-loading__shimmer--chart" />
        </div>
      </div>
    </div>
  );
};
